import type { AnalyticsPlugin } from 'analytics';
import { EVENTS } from '../constants/events';

// Declaração global para fbq
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

interface MetaPixelConfig {
  pixelId: string;
  options?: {
    debug?: boolean;
  };
}

// Singleton para controlar inicialização única do Meta Pixel
class MetaPixelManager {
  private static instance: MetaPixelManager;
  private isInitialized = false;
  private currentPixelId: string | null = null;
  // PRAT-83: PageView pedido antes de o pixel ficar pronto (sai no init, com o mesmo eventID).
  private pendingPageViewId: string | null = null;

  private constructor() {}

  static getInstance(): MetaPixelManager {
    if (!MetaPixelManager.instance) {
      MetaPixelManager.instance = new MetaPixelManager();
    }
    return MetaPixelManager.instance;
  }

  async initialize(pixelId: string, debug = false): Promise<boolean> {
    console.log('[Meta Pixel] 🔍 Iniciando processo de inicialização', {
      pixelId,
      debug,
      isInitialized: this.isInitialized,
      currentPixelId: this.currentPixelId,
    });

    // Se já foi inicializado com o mesmo pixel ID, não faz nada
    if (this.isInitialized && this.currentPixelId === pixelId) {
      console.log('[Meta Pixel] ✅ Já inicializado com o mesmo ID, pulando reinicialização');
      return true;
    }

    // Se já foi inicializado com ID diferente, não reinicializa
    if (this.isInitialized && this.currentPixelId !== pixelId) {
      console.warn('[Meta Pixel] ⚠️ Já inicializado com ID diferente', {
        current: this.currentPixelId,
        novo: pixelId,
      });
      return true;
    }

    try {
      console.log('[Meta Pixel] 🚀 Configurando pixel:', pixelId);

      // Verificar se fbq está disponível (carregado via HTML)
      if (!window.fbq) {
        console.warn('[Meta Pixel] ⚠️ fbq não disponível - script não carregou');
        this.isInitialized = true;
        this.currentPixelId = pixelId;
        return false;
      }

      // Log detalhado do estado do fbq
      console.log('[Meta Pixel] 🔍 Estado do fbq:', {
        exists: !!window.fbq,
        type: typeof window.fbq,
        isFunction: typeof window.fbq === 'function',
        // @ts-expect-error - acessar propriedades internas para debug
        hasQueue: !!(window.fbq && window.fbq.queue),
        // @ts-expect-error - acessar propriedades internas para debug
        queueLength: window.fbq && window.fbq.queue ? window.fbq.queue.length : 0,
      });

      // Verificar se o pixel foi inicializado pelo script HTML
      console.log('[Meta Pixel] 🔍 Verificando inicialização do pixel...');
      // @ts-expect-error - verificar se fbq foi inicializado
      if (window.fbq && window.fbq.callMethod) {
        console.log('[Meta Pixel] ✅ fbq.callMethod disponível - pixel inicializado');
      } else {
        console.warn(
          '[Meta Pixel] ⚠️ fbq.callMethod não disponível - pixel pode não estar inicializado'
        );
      }

      // Inicializar o pixel se necessário
      console.log('[Meta Pixel] 🔧 Inicializando pixel com ID:', pixelId);
      try {
        // PRAT-83: sem os "eventos automáticos" da Meta. Ligados, o Pixel manda
        // SubscribedButtonClick em todo clique de botão (adicionar sem cumprir as
        // regras do item, endereço, login antes de entrar...). O funil já vai
        // completo, e com eventID, pelos eventos padrão e pela API de Conversões.
        window.fbq('set', 'autoConfig', false, pixelId);
        window.fbq('init', pixelId);
        console.log('[Meta Pixel] ✅ Pixel inicializado com sucesso');
      } catch (initError) {
        console.error('[Meta Pixel] ❌ Erro ao inicializar pixel:', initError);
      }

      // PageView inicial: o da página atual, com o eventID que a API de Conversões
      // também recebe (PRAT-83). Sem ele, o MetaPageViewTracker manda logo em seguida.
      try {
        if (this.pendingPageViewId) {
          window.fbq('track', 'PageView', {}, { eventID: this.pendingPageViewId });
          this.pendingPageViewId = null;
        }
        console.log('[Meta Pixel] ✅ PageView inicial enviado');
      } catch (pageViewError) {
        console.error('[Meta Pixel] ❌ Erro ao enviar PageView:', pageViewError);
      }

      // O script no HTML já inicializa o pixel automaticamente
      // Apenas adicionamos o noscript fallback e configuramos o plugin
      this.addNoscriptFallback(pixelId);

      // Aguardar um pouco para garantir que o pixel está funcionando
      setTimeout(() => {
        console.log('[Meta Pixel] 🔍 Verificação final:', {
          fbq: !!window.fbq,
          pixelId,
          scripts: Array.from(document.scripts).filter(s => s.src.includes('facebook')).length,
        });
      }, 1000);

      console.log('[Meta Pixel] ✅ Configurado com sucesso!');
      console.log(
        '%c✅ META PIXEL FUNCIONANDO!',
        'font-size: 16px; color: green; font-weight: bold; background: #e8f5e9; padding: 10px; border-radius: 5px;'
      );

      // Log para debug do Pixel Helper
      console.log('[Meta Pixel] 🔍 Debug info:', {
        fbq: !!window.fbq,
        pixelId,
        userAgent: navigator.userAgent,
        url: window.location.href,
      });

      this.isInitialized = true;
      this.currentPixelId = pixelId;

      return true;
    } catch (error) {
      console.error('[Meta Pixel] ❌ Erro na inicialização:', error);
      console.error('[Meta Pixel] 🔍 Stack trace:', error instanceof Error ? error.stack : 'N/A');

      // Em caso de erro
      console.log('[Meta Pixel] 🔄 Erro na inicialização');

      return false;
    }
  }

  private addNoscriptFallback(pixelId: string): void {
    try {
      const existingNoscript = document.querySelector(`noscript[data-pixel-id="${pixelId}"]`);
      if (existingNoscript) {
        return;
      }

      // Criar elemento noscript
      const noscript = document.createElement('noscript');
      noscript.setAttribute('data-pixel-id', pixelId);

      // Imagem de fallback como TEXTO do noscript (PRAT-83). Um <img> criado
      // com src é baixado na hora, mesmo dentro do noscript: era um PageView a
      // mais, sem eventID, em toda abertura — a Meta contava a página duas vezes.
      noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1" />`;

      // Adicionar ao body
      document.body.appendChild(noscript);

      console.log('[Meta Pixel] ✅ Noscript fallback adicionado:', pixelId);
    } catch (error) {
      console.warn('[Meta Pixel] ⚠️ Erro ao adicionar noscript fallback:', error);
    }
  }

  track(event: string, data?: Record<string, unknown>) {
    if (!this.isInitialized) {
      console.warn('[Meta Pixel] ⚠️ Tentativa de rastrear evento antes da inicialização', {
        event,
        isInitialized: this.isInitialized,
      });
      return;
    }

    try {
      console.log('[Meta Pixel] 📊 Rastreando evento:', { event, data });

      if (window.fbq) {
        // Log detalhado para debug
        console.log('[Meta Pixel] 🔍 Debug antes de enviar:', {
          fbq: !!window.fbq,
          event,
          data,
          pixelId: this.currentPixelId,
          url: window.location.href,
        });

        // Verificar se fbq é uma função
        console.log('[Meta Pixel] 🔍 Tipo do fbq:', typeof window.fbq);
        console.log('[Meta Pixel] 🔍 fbq é função?', typeof window.fbq === 'function');

        try {
          // Chamar fbq com logs detalhados
          console.log('[Meta Pixel] 🚀 Executando: window.fbq("track", "' + event + '", data)');
          console.log('[Meta Pixel] 🔍 Dados completos:', JSON.stringify(data, null, 2));
          console.log('[Meta Pixel] 🔍 Pixel ID atual:', this.currentPixelId);

          // CHAMAR FBQ DIRETAMENTE (sem optional chaining para garantir execução)
          // PRAT-83: event_id vira o eventID do Pixel (deduplicação com a API de Conversões).
          const { event_id: eventId, ...payload } = data ?? {};
          if (typeof eventId === 'string' && eventId) {
            window.fbq('track', event, payload, { eventID: eventId });
          } else {
            window.fbq('track', event, payload);
          }

          console.log('[Meta Pixel] ✅ window.fbq("track", "' + event + '") EXECUTADO');
          console.log('[Meta Pixel] 🌐 Verificar Network tab agora! Filtrar por: facebook.com/tr');
          console.log(
            '[Meta Pixel] 🔍 URL esperada: https://www.facebook.com/tr?id=' +
              this.currentPixelId +
              '&ev=' +
              event
          );
        } catch (fbqError) {
          console.error('[Meta Pixel] ❌ ERRO ao chamar fbq:', fbqError);
          console.error(
            '[Meta Pixel] 🔍 Stack trace:',
            fbqError instanceof Error ? fbqError.stack : 'N/A'
          );
          return;
        }

        // Verificar após um delay
        setTimeout(() => {
          console.log('[Meta Pixel] ⏱️ Após 500ms - evento deveria ter sido enviado');
          console.log('[Meta Pixel] 🔍 Verifique Network tab para confirmar requisição HTTP');
        }, 500);

        console.log('[Meta Pixel] ✅ Evento rastreado - aguardando envio para Facebook');
      } else {
        console.warn('[Meta Pixel] ⚠️ fbq não disponível');
      }
    } catch (error) {
      console.error('[Meta Pixel] ❌ Erro ao rastrear evento:', { event, error });
    }
  }

  trackCustom(event: string, data?: Record<string, unknown>) {
    if (!this.isInitialized) {
      console.warn(
        '[Meta Pixel] ⚠️ Tentativa de rastrear evento customizado antes da inicialização',
        { event, isInitialized: this.isInitialized }
      );
      return;
    }

    try {
      console.log('[Meta Pixel] 📊 Rastreando evento customizado:', { event, data });

      if (window.fbq) {
        window.fbq('trackCustom', event, data);
        console.log('[Meta Pixel] ✅ Evento customizado rastreado');
      } else {
        console.warn('[Meta Pixel] ⚠️ fbq não disponível');
      }
    } catch (error) {
      console.error('[Meta Pixel] ❌ Erro ao rastrear evento customizado:', { event, error });
    }
  }

  pageView(eventId?: string) {
    if (!this.isInitialized) {
      if (eventId) this.pendingPageViewId = eventId;
      console.warn('[Meta Pixel] ⚠️ PageView não rastreado - não inicializado');
      return;
    }

    try {
      console.log('[Meta Pixel] 📄 Rastreando PageView');

      if (window.fbq) {
        if (eventId) {
          window.fbq('track', 'PageView', {}, { eventID: eventId });
        } else {
          window.fbq('track', 'PageView');
        }
        console.log('[Meta Pixel] ✅ PageView rastreado');
      } else {
        console.warn('[Meta Pixel] ⚠️ fbq não disponível');
      }
    } catch (error) {
      console.error('[Meta Pixel] ❌ Erro ao rastrear PageView:', error);
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }
}

const metaPixelManager = MetaPixelManager.getInstance();

/** PageView do Pixel com o mesmo id do evento da API de Conversões (PRAT-83). */
export function metaPixelPageView(eventId: string) {
  metaPixelManager.pageView(eventId);
}

export default class MetaPixelPlugin implements AnalyticsPlugin {
  public name = 'meta-pixel';
  public config: MetaPixelConfig;
  private isInitialized = false;
  [key: string]: unknown;

  constructor(config: MetaPixelConfig) {
    this.config = config;
  }

  async initialize() {
    console.log('[Meta Pixel Plugin] 🔧 Inicializando plugin', { config: this.config });

    if (!this.config.pixelId) {
      console.error('[Meta Pixel Plugin] ❌ Nenhum pixelId fornecido na configuração');
      return;
    }

    const success = await metaPixelManager.initialize(
      this.config.pixelId,
      this.config.options?.debug
    );

    this.isInitialized = success;
    console.log('[Meta Pixel Plugin] 📍 Status após inicialização:', {
      success,
      isInitialized: this.isInitialized,
    });

    if (!success) {
      console.error('[Meta Pixel Plugin] ❌ Falha na inicialização do plugin');
    }
  }

  page() {
    console.log('[Meta Pixel Plugin] 📄 Método page() chamado', {
      isInitialized: this?.isInitialized,
      hasManager: !!metaPixelManager,
    });

    // PRAT-83: o PageView do Meta sai do MetaPageViewTracker (toda troca de
    // página, com o eventID que a API de Conversões também recebe). Mandar aqui
    // de novo, sem eventID, contaria a mesma página duas vezes.
  }

  track({ payload }: { payload: { event: string; properties?: Record<string, unknown> } }) {
    console.log('[Meta Pixel Plugin] 🎯 Método track() chamado', {
      payload,
      isInitialized: this?.isInitialized,
    });

    if (!this?.isInitialized || !metaPixelManager) {
      console.warn('[Meta Pixel Plugin] ⚠️ Evento não rastreado - plugin não inicializado');
      return;
    }

    const { event, properties } = payload;

    switch (event) {
      case EVENTS.PAGE_VIEW:
        metaPixelManager.pageView();
        break;
      case EVENTS.VIEW_CONTENT:
        metaPixelManager.track('ViewContent', properties);
        break;
      case EVENTS.ADD_TO_CART:
        metaPixelManager.track('AddToCart', properties);
        break;
      case EVENTS.INITIATE_CHECKOUT:
        metaPixelManager.track('InitiateCheckout', properties);
        break;
      case EVENTS.ADD_PAYMENT_INFO:
        metaPixelManager.track('AddPaymentInfo', properties);
        break;
      case EVENTS.PURCHASE:
        metaPixelManager.track('Purchase', properties);
        break;
      // PRAT-19: busca e cadastro viram os eventos padrão do Meta.
      case EVENTS.SEARCH:
        metaPixelManager.track('Search', properties);
        break;
      case EVENTS.SIGN_UP:
        metaPixelManager.track('CompleteRegistration', properties);
        break;
      case EVENTS.LEAD:
        metaPixelManager.track('Lead', properties);
        break;
      case EVENTS.ORDER_CREATED:
        metaPixelManager.trackCustom('OrderCreated', properties);
        break;
      case EVENTS.ORDER_ACCEPTED:
        metaPixelManager.trackCustom('OrderAccepted', properties);
        break;
      case EVENTS.ORDER_OUT_FOR_DELIVERY:
        metaPixelManager.trackCustom('OrderOutForDelivery', properties);
        break;
      case EVENTS.ORDER_READY:
        metaPixelManager.trackCustom('OrderReady', properties);
        break;
      case EVENTS.ORDER_DELIVERED:
        metaPixelManager.trackCustom('OrderDelivered', properties);
        break;
      case EVENTS.ORDER_CANCELED:
        metaPixelManager.trackCustom('OrderCanceled', properties);
        break;
      case EVENTS.VIEW_ITEM_LIST:
        metaPixelManager.trackCustom('ViewItemList', properties);
        break;
      case EVENTS.SELECT_ITEM:
        metaPixelManager.trackCustom('SelectItem', properties);
        break;
      case EVENTS.ADD_SHIPPING_INFO:
        metaPixelManager.trackCustom('AddShippingInfo', properties);
        break;
      case EVENTS.PRATIGO_FOOTER_CLICK:
        metaPixelManager.trackCustom('PratigoFooterClick', properties);
        break;
      default:
        metaPixelManager.trackCustom(event, properties);
    }
  }

  identify() {}

  loaded() {
    return Boolean(this?.isInitialized);
  }

  ready() {
    return Boolean(this?.isInitialized) && metaPixelManager?.isReady();
  }
}
